import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap
} from 'rxjs';

import { ConfirmationModalComponent } from '@app/core/layouts/components/confirmation-modal/confirmation-modal.component';
import { ModalService } from '@app/core/ui/components/modal';
import { makeQueryParams } from '@app/shared/helpers/make-query-params';

import { PlatformEnum } from '../enums/platform.enum';
import { HttpResource } from './api/resource/http-resource';

const CONFIRMATION_MODAL_ENDPOINT = '/ConfirmationModal';
const CONFIRMATION_MODAL_CONFIRM_ENDPOINT = '/ConfirmationModal/confirm';

/** Valor de página usado na API para modal exibido em qualquer rota. */
const CONFIRMATION_MODAL_PAGE_ALL = 'todas';

export type ConfirmationModalCta = {
  name: string;
  link?: string;
};

export type ConfirmationModalData = {
  id: string;
  content: string;
  title: string;
  cta: ConfirmationModalCta;
};

export type ConfirmationModalInput = ConfirmationModalData;

export const CONFIRMATION_MODAL_ROUTE_DATA_KEY = 'confirmationModalRoute';

@Injectable({ providedIn: 'root' })
export class ConfirmationModalService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpResource);
  private readonly modal = inject(ModalService);

  /**
   * Emite uma vez quando a primeira rota foi processada e o modal de confirmação
   * NÃO foi aberto. Use para exibir outros modais (ex.: last days reminder) apenas
   * quando o modal de confirmação não for exibido.
   */
  private readonly otherModalsAllowed = new Subject<void>();
  readonly otherModalsAllowed$ = this.otherModalsAllowed.asObservable();

  /**
   * Inicia a observação das rotas no main layout.
   * Na entrada inicial e a cada NavigationEnd, chama a API com a página atual (path, sem query)
   * e, se houver modal de confirmação e ainda não foi exibido, abre o modal.
   * Na primeira navegação: se o modal de confirmação não for aberto, emite otherModalsAllowed$.
   */
  startWatchingRoutes(): void {
    let isFirstNavigation = true;

    merge(
      of(this.getCurrentRouteParamForApi()),
      this.router.events.pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        map((event) => this.getRouteParamForApi(event))
      )
    )
      .pipe(
        distinctUntilChanged(),
        switchMap((route) =>
          this.getModalConfigByRoute(route).pipe(
            switchMap((routeModal) =>
              routeModal !== null
                ? of(routeModal)
                : this.getModalConfigByRoute(CONFIRMATION_MODAL_PAGE_ALL)
            )
          )
        )
      )
      .subscribe((data) => {
        const opened = !!data;
        if (opened) {
          queueMicrotask(() => {
            this.openModal(data);
          });
        }
        if (isFirstNavigation) {
          isFirstNavigation = false;
          if (!opened) {
            this.otherModalsAllowed.next();
          }
        }
      });
  }

  /**
   * Busca na API o modal de confirmação para a rota informada.
   */
  getModalConfigByRoute(
    route: string
  ): Observable<ConfirmationModalData | null> {
    return this.http
      .get<ConfirmationModalData | null>(CONFIRMATION_MODAL_ENDPOINT, {
        params: makeQueryParams({
          platform: PlatformEnum.MyWorld,
          page: route
        })
      })
      .pipe(catchError(() => of(null)));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  openModal({ title: _, ...data }: ConfirmationModalData): void {
    this.modal.open(ConfirmationModalComponent, {
      data: { ...data },
      options: {
        disableBackdropClose: true,
        enableFullscreenOnMobile: true
      }
    });
  }

  /**
   * Chama a API para marcar o modal de confirmação como visto/confirmado.
   * Deve ser chamado quando o usuário clicar no botão do modal (envia apenas o id).
   */
  markAsSeen(id: string) {
    return this.http.post<void>(
      `${CONFIRMATION_MODAL_CONFIRM_ENDPOINT}/${id}`,
      null
    );
  }

  /**
   * Retorna o identificador da página (path, sem query params) a ser enviado à API:
   * primeiro tenta `route.data[CONFIRMATION_MODAL_ROUTE_DATA_KEY]`, senão usa o path da URL.
   */
  private getRouteParamForApi(event: NavigationEnd): string {
    const fromData = this.getRouteParamFromData();
    if (fromData !== null) {
      return fromData;
    }
    const fullUrl = event.urlAfterRedirects ?? event.url ?? this.router.url;
    return this.stripQueryAndHash(fullUrl);
  }

  /**
   * Retorna o identificador da página atual (path, sem query params) para uso na entrada inicial.
   */
  private getCurrentRouteParamForApi(): string {
    const fromData = this.getRouteParamFromData();
    if (fromData !== null) {
      return fromData;
    }
    return this.stripQueryAndHash(this.router.url);
  }

  /**
   * Remove query string e hash da URL, deixando apenas o path, e tira a barra inicial.
   */
  private stripQueryAndHash(url: string): string {
    const pathOnly = url.split('?')[0].split('#')[0];
    return pathOnly.replace(/^\//, '');
  }

  /**
   * Lê o identificador em `route.data[CONFIRMATION_MODAL_ROUTE_DATA_KEY]`.
   * Retorna null se não existir ou for vazio.
   */
  private getRouteParamFromData(): string | null {
    let snapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    const value = snapshot.data[CONFIRMATION_MODAL_ROUTE_DATA_KEY];
    return value !== null && value !== undefined && value !== ''
      ? String(value)
      : null;
  }
}
